import React, { useState } from "react";
import { Alert, Button, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons";
import FaIcon from "../components/FaIcon";
import { AuthorizeWebResponse } from "../api/types";
import { verifyLoginBackupCode, verifyLoginTotp } from "../Profile/Tfa/TfaApi";

interface TfaChallengeProps {
  username: string;
  onSuccess: (response: AuthorizeWebResponse) => void;
  onCancel: () => void;
}

type Mode = "totp" | "backup";

const BACKUP_CODE_LENGTH = 10;

export default function TfaChallenge({ username, onSuccess, onCancel }: TfaChallengeProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function switchMode(next: Mode) {
    setMode(next);
    setCode("");
    setBackupCode("");
    setError(null);
  }

  async function handleVerify(submittedCode?: string) {
    const value = submittedCode ?? (mode === "totp" ? code : backupCode);
    const minLen = mode === "totp" ? 6 : BACKUP_CODE_LENGTH;
    if (value.length < minLen) return;
    setSubmitting(true);
    setError(null);
    try {
      const verify = mode === "totp" ? verifyLoginTotp : verifyLoginBackupCode;
      const { ok, data } = await verify(value);
      if (ok && data.status === "success") {
        onSuccess(data);
      } else {
        setError(data.description ?? t("The code you entered is not valid."));
        if (mode === "totp") setCode("");
        else setBackupCode("");
      }
    } catch {
      setError(t("Could not verify the code. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-center text-center">
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "rgba(0, 153, 64, 0.08)",
          color: "var(--primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
          marginBottom: 16,
        }}
      >
        <FaIcon icon={faShieldCheck} />
      </div>

      <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
        {t("Two-factor authentication")}
      </Typography.Title>
      <Typography.Paragraph style={{ marginBottom: 4, color: "rgba(0,0,0,0.65)" }}>
        {mode === "totp"
          ? t("Enter the 6-digit code from your authenticator app for")
          : t("Enter one of your backup codes for")}
      </Typography.Paragraph>
      <Typography.Paragraph style={{ marginBottom: 24 }}>
        <Typography.Text strong>{username}</Typography.Text>
      </Typography.Paragraph>

      {mode === "totp" ? (
        <div style={{ marginBottom: 20 }}>
          <Input.OTP
            autoFocus
            disabled={submitting}
            length={6}
            size="large"
            style={{ gap: 10 }}
            value={code}
            onChange={(v) => {
              setCode(v);
              setError(null);
            }}
            onInput={(values) => {
              const joined = values.join("");
              if (joined.length === 6 && !submitting) {
                void handleVerify(joined);
              }
            }}
          />
        </div>
      ) : (
        <div style={{ width: "100%", marginBottom: 20 }}>
          <Input
            autoFocus
            disabled={submitting}
            maxLength={BACKUP_CODE_LENGTH}
            placeholder={t("Enter backup code")}
            size="large"
            style={{
              fontFamily: "monospace",
              textAlign: "center",
            }}
            value={backupCode}
            onChange={(e) => {
              setBackupCode(e.target.value.toUpperCase().replace(/\s+/g, ""));
              setError(null);
            }}
            onPressEnter={() => void handleVerify()}
          />
        </div>
      )}

      {error && (
        <Alert
          message={error}
          showIcon
          style={{ width: "100%", marginBottom: 16, textAlign: "left" }}
          type="error"
        />
      )}

      <Button
        block
        className="login-button"
        disabled={mode === "totp" ? code.length < 6 : backupCode.length < BACKUP_CODE_LENGTH}
        loading={submitting}
        type="primary"
        onClick={() => void handleVerify()}
      >
        {t("Verify")}
      </Button>
      <Button
        block
        disabled={submitting}
        style={{ marginTop: 8 }}
        type="link"
        onClick={() => switchMode(mode === "totp" ? "backup" : "totp")}
      >
        {mode === "totp" ? t("Use a backup code") : t("Use authenticator code")}
      </Button>
      <Button
        block
        disabled={submitting}
        style={{ marginTop: 0 }}
        type="link"
        onClick={onCancel}
      >
        {t("Use a different account")}
      </Button>
    </div>
  );
}
